// The one model of where you can go: the workspace's core pages, its packs'
// pages, the fixed extras (captures, music, trash, settings…) and the pinned
// pages and lists. The desktop sidebar groups it into sections; the phone's
// launcher, dock and home screens treat each entry as an app. Keeping one list
// means a page cannot be in the sidebar under one name and on the phone under
// another.
import { PrimeMark } from '@/components/PrimeMark'
import {
  FileText,
  FolderKanban,
  Library,
  House,
  ListChecks,
  Settings,
  Timer,
  HeartPulse,
  CalendarHeart,
  Users,
  Network,
  Zap,
  Gift,
  Palette,
} from 'lucide-react'
import { useCurrentWorkspace, useLists, usePages, useProjects, useWorkspaces } from '@/db/hooks'
import { sortPages } from '@/kinds/page/components'
import { sortLists } from '@/kinds/list/components'
import { useScope } from '@/state/scope'
import { useAccess } from '@/state/access'
import { navForPacks, packsFor } from '@/packs'
import { ALL } from '@/kinds/workspace/schema'
import { DAY_LABEL, dayOf, type SuiteDay } from '@/suites'
import {
  CORE_NAV,
  MERGED_INTO,
  NAV_SECTIONS,
  isPinnedNav,
  laidOutNav,
  navIconOf,
  pinnedProjects,
  type NavItem,
} from './nav'

/** Pages that are not a workspace's choice: always there, like Settings. */
const EXTRAS: NavItem[] = [
  // the library: music first, then films, TV and games (online only)
  { key: 'music', to: '/music', label: 'Library', icon: '🎧', Icon: Library, section: 'more' },
  // the pomodoro, pinned on every page while it runs
  { key: 'focus', to: '/focus', label: 'Focus', icon: '⏱', Icon: Timer, section: 'more' },
  // Prime: straight into its thread (/agents lands there; Agents is the list of every agent)
  { key: 'prime', to: '/agents', label: 'Prime', icon: '✦', Icon: PrimeMark, section: 'more', exact: true },
  // every agent as a live graph around Prime, for a window kept open beside other work
  { key: 'graph', to: '/agents/graph', label: 'Graph', icon: '⌘', Icon: Network, section: 'more' },
  // dates: birthdays, appointments, renewals, countdowns (not tasks)
  // people: everyone in the owner's life, and everything linked to each of them
  { key: 'people', to: '/people', label: 'People', icon: '☺', Icon: Users, section: 'plan' },
  { key: 'dates', to: '/dates', label: 'Dates', icon: '🗓', Icon: CalendarHeart, section: 'plan' },
  // what the agents' merged changes did, with their before and after screenshots
  { key: 'whatsnew', to: '/whats-new', label: "What's new", icon: '🎁', Icon: Gift, section: 'more' },
  { key: 'wellbeing', to: '/wellbeing', label: 'Wellbeing', icon: '🌙', Icon: HeartPulse, section: 'more' },
  // a workspace member's own Prime (a separate one, fenced to her workspace); only members are offered it
  { key: 'ask', to: '/ask', label: 'Prime', icon: '✦', Icon: PrimeMark, section: 'more' },
  // the themes studio: saved themes, a gallery of everyone's in the suite, and the editor
  { key: 'themes', to: '/themes', label: 'Themes', icon: '🎨', Icon: Palette, section: 'more' },
  { key: 'settings', to: '/settings', label: 'Settings', icon: '⚙', Icon: Settings, section: 'more' },
]

/** Core pages a workspace member does not get: the assistant's. */
const MEMBER_HIDDEN = new Set<string>(['agents', 'prime', 'graph'])

/**
 * What an admin member (2026-10-17) never gets in her nav: the owner's own agents,
 * the factory's news and his wellbeing. The server refuses them to her as well.
 */
const ADMIN_HIDDEN = new Set<string>(['agents', 'prime', 'graph', 'whatsnew', 'wellbeing'])
const forAdmin = (n: NavItem) => !ADMIN_HIDDEN.has(n.key) && n.section !== 'dev' && n.section !== 'infra'

/** The phone's home screens: an app on the phone, not a sidebar entry. */
export const HOME_APP: NavItem = {
  key: 'home',
  to: '/home',
  label: 'Home',
  icon: '▦',
  Icon: House,
  section: 'plan',
  exact: true,
}

export { isPinnedNav }

/** The core keys a hand-set `nav` turns on, with what rides along; null is all of them. */
const enabledNav = (nav: readonly string[] | undefined): Set<string> | null => {
  if (!nav) return null
  const on = new Set<string>(nav)
  // lists ride with pages: workspaces configured before lists existed keep them without editing nav
  if (on.has('pages')) on.add('lists')
  // a merged view a workspace chose brings the app it now lives in (Someday → Review)
  for (const k of [...on]) if (MERGED_INTO[k]) on.add(MERGED_INTO[k]!)
  return on
}

const packNavItems = (packNav: ReturnType<typeof navForPacks>): NavItem[] =>
  packNav.map((n) => ({
    key: n.to,
    to: n.to,
    label: n.label,
    icon: n.icon,
    Icon: n.Icon,
    section: n.section ?? ('connected' as const),
    ...(n.exact ? { exact: true } : {}),
  }))

/**
 * Today's entry as the suite's day page names it: Life's and Blank's are
 * Today, DevOS's is Now (suites/index.ts `day`). The route stays `/`.
 */
const withDay = (core: NavItem[], day: SuiteDay): NavItem[] =>
  day === 'work'
    ? core.map((n) =>
        n.key === 'today' ? { ...n, label: DAY_LABEL.work, short: undefined, Icon: Zap, icon: '⚡' } : n,
      )
    : core

/** The apps a suite with this spec has (Home first), for drawing a suite other than the current one. */
export const appsFor = (spec: {
  nav?: readonly string[]
  packs: readonly string[]
  suite?: string
}): NavItem[] => {
  const on = enabledNav(spec.nav)
  const core = withDay(
    on ? CORE_NAV.filter((n) => on.has(n.key)) : CORE_NAV,
    dayOf({ spec: { suite: spec.suite, packs: [...spec.packs] } }, ''),
  )
  const packs = [...spec.packs]
  const laid = laidOutNav(
    core,
    packNavItems(navForPacks(packs)),
    EXTRAS.filter((n) => n.key !== 'ask'),
    packsFor(packs).flatMap((p) => p.layout ?? []),
    { workspace: true, handSet: !!on },
  )
  return [HOME_APP, ...laid.items]
}

/**
 * The app ids a suite with this spec has on the owner's phone (Home first;
 * no pinned things, which a new suite has none of): what `useNav().apps`
 * gives once the device is in it. Used to fit a manifest's Home before the
 * suite exists.
 */
export const appIdsFor = (spec: { nav?: readonly string[]; packs: readonly string[] }): string[] =>
  appsFor(spec).map((n) => n.key)

export const useNav = () => {
  const scope = useScope((s) => s.ws)
  const current = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const pagesQ = usePages()
  const listsQ = useLists()
  const projectsQ = useProjects()
  // Everything takes every workspace's packs, once each (two dev workspaces are one Dev section)
  const packNames =
    current?.spec.packs ?? (scope === ALL ? [...new Set(workspaces.flatMap((w) => w.spec.packs))] : [])
  const packNav = navForPacks(packNames)
  const layouts = packsFor(packNames).flatMap((p) => p.layout ?? [])
  const enabledKeys = enabledNav(current?.spec.nav)
  const coreNav = withDay(
    enabledKeys ? CORE_NAV.filter((n) => enabledKeys.has(n.key)) : CORE_NAV,
    dayOf(current, scope),
  )
  const packItems = packNavItems(packNav)
  // pinned pages sit under "All pages" in the sidebar
  const pinnedPages = sortPages((pagesQ ?? []).filter((p) => p.spec.pinned && !p.deleted_at)).slice(0, 12)
  const pageNav: NavItem[] = pinnedPages.map((p) => ({
    key: `page-${p.id}`,
    to: `/pages/${p.id}`,
    label: p.name,
    ...navIconOf(p.spec.icon, FileText, ''),
    section: 'pages' as const,
    exact: true,
  }))
  // pinned lists sit under "Lists" the same way
  const pinnedLists = sortLists(
    (listsQ ?? []).filter((l) => l.spec.pinned && !l.deleted_at && !l.status.converted_to),
  ).slice(0, 8)
  const listNav: NavItem[] = pinnedLists.map((l) => ({
    key: `list-${l.id}`,
    to: `/lists/${l.id}`,
    label: l.spec.title,
    ...navIconOf(l.spec.icon, ListChecks, '☑'),
    section: 'pages' as const,
    exact: true,
  }))
  // pinned projects sit under the Projects section
  const projectNav: NavItem[] = pinnedProjects(projectsQ).map((p) => ({
    key: `project-${p.id}`,
    to: `/projects/${p.id}`,
    label: p.spec.title,
    ...navIconOf(p.spec.icon, FolderKanban, '▣'),
    section: 'projects' as const,
    exact: true,
  }))
  // a workspace member gets the shared workspace's own pages and Settings (to sign out): no
  // assistant, library, people, dates, wellbeing or packs, which are the owner's
  const member = useAccess((s) => s.member)
  const admin = useAccess((s) => s.admin)
  // a pack's layout is one workspace's: Everything keeps the plain sections
  const laid = admin
    ? // an admin member has the owner's whole app but what is his alone, and her own Prime
      laidOutNav(coreNav.filter(forAdmin), packItems.filter(forAdmin), EXTRAS.filter(forAdmin), layouts, {
        workspace: !!current,
        handSet: !!enabledKeys,
      })
    : member
      ? {
          items: [
            ...coreNav.filter((n) => !MEMBER_HIDDEN.has(n.key)),
            ...EXTRAS.filter((n) => n.key === 'ask' || n.key === 'settings'),
          ],
          order: NAV_SECTIONS,
        }
      : laidOutNav(
          coreNav,
          packItems,
          EXTRAS.filter((n) => n.key !== 'ask'),
          layouts,
          {
            workspace: !!current,
            handSet: !!enabledKeys,
          },
        )
  const sideNav = laid.items
  const sections = laid.order
    .map((sec) => ({
      ...sec,
      items: [
        ...sideNav.filter((n) => n.section === sec.key),
        ...(sec.key === 'pages' ? [...pageNav, ...listNav] : []),
        ...(sec.key === 'projects' ? projectNav : []),
      ],
    }))
    .filter((sec) => sec.items.length)
  /** everything, as the phone's apps: Home first, pinned things last */
  const apps: NavItem[] = [HOME_APP, ...sideNav, ...pageNav, ...listNav, ...projectNav]
  // the ⌘K palette's views: every core page and the packs' for the owner (a hand-set nav narrows the
  // sidebar, not the palette); a member gets only what her sidebar has, never a pack's owner pages
  const paletteNav: NavItem[] = member
    ? sideNav.filter((n) => n.key !== 'settings') // the palette has its own Settings line
    : [...CORE_NAV, ...packItems, ...EXTRAS.filter((n) => n.key === 'whatsnew')]
  return {
    coreNav,
    packNav: admin ? packItems.filter(forAdmin) : member ? [] : packItems,
    paletteNav,
    sections,
    apps,
    pinned: [...pageNav, ...listNav, ...projectNav],
  }
}
